/**
 * Utility functions for validating Nosto products and SKUs
 * to prevent rendering of products without valid parent references or available inventory
 */

/**
 * Validates if a product has a valid parent product reference
 * @param {Object} product - The product object
 * @returns {boolean} - True if valid parent reference exists or if it's a marketplace product
 */
export const hasValidParentProduct = (product) => {
    // Allow marketplace products (products without brand 'Monica + Andy')
    const isMarketplaceProduct = product?.brand && product.brand !== 'Monica + Andy'

    if (isMarketplaceProduct) {
        return true
    }

    // For Monica + Andy products, check for valid parentProductId in tags1 (case insensitive)
    const hasParentProductId = product?.tags1?.some(tag => {
        // Check for case-insensitive parentProductId variations
        if (tag.toLowerCase().includes('parentproductid_')) {
            const parentId = tag.split('_')[1]
            return parentId && parentId.length > 0 && parentId !== 'undefined' && parentId !== 'null'
        }
        return false
    })

    // If Monica + Andy product has parentProductId, validate it
    if (hasParentProductId) {
        return true
    }

    // For Monica + Andy products without parentProductId, allow them to pass
    // (many products don't have this tag but are still valid)
    return true
}

/**
 * Validates if a product has available SKUs with inventory or valid exceptions
 * @param {Object} product - The product object
 * @returns {boolean} - True if product has valid available SKUs
 */
export const hasValidAvailableSkus = (product) => {
    const isMarketplaceProduct = product?.brand && product.brand !== 'Monica + Andy'

    // For marketplace products without SKUs, check product-level availability
    if (isMarketplaceProduct && (!product?.skus || product.skus.length === 0)) {
        return product.availability === 'InStock'
    }

    return product?.skus?.some(sku => {
        // Check basic SKU validity
        if (!sku.id || !sku.name) return false

        // For bundle products and virtual classes, allow even if inventory is 0
        const isBundle = product?.tags1?.includes('bundle-builder-product')
        const isVirtualClass = sku.customFields?.find(f => f.key === 'producttype')?.value === 'Virtual Classes'
        const isComingSoon = product?.tags1?.some(tag =>
            tag.includes('comingsoon') || tag.includes('coming_soon')
        )

        if (isBundle || isVirtualClass || isComingSoon) {
            return true
        }

        // For marketplace products, check product-level availability instead of SKU inventory
        if (isMarketplaceProduct) {
            return product.availability === 'InStock'
        }

        // For regular products, require inventory > 0
        return sku.inventoryLevel > 0
    })
}

/**
 * Validates basic product structure and completeness
 * @param {Object} product - The product object
 * @returns {boolean} - True if product has valid basic structure
 */
export const hasValidProductStructure = (product) => {
    const isMarketplaceProduct = product?.brand && product.brand !== 'Monica + Andy'

    return product?.name &&
           product?.productId &&
           (isMarketplaceProduct || (product?.skus && Array.isArray(product.skus) && product.skus.length > 0)) &&
           !product.name.includes('[hidden]') &&
           !product.name.includes('[HIDDEN]')
}

/**
 * Validates if a SKU is valid for rendering
 * @param {Object} sku - The SKU object
 * @param {Object} product - The parent product object
 * @returns {boolean} - True if SKU is valid for rendering
 */
export const hasValidSkuData = (sku, product) => {
    const isMarketplaceProduct = product?.brand && product.brand !== 'Monica + Andy'

    // For marketplace products, be more lenient with validation
    if (isMarketplaceProduct) {
        // If no SKU object is provided, check product-level availability
        if (!sku || !sku.id) {
            return product.availability === 'InStock'
        }

        // For marketplace products with SKU, only require basic SKU data
        // Don't require product_id for marketplace products as they might not have it
        const hasBasicSkuData = sku.id && sku.name

        return hasBasicSkuData
    }

    // For Monica + Andy products, require all fields
    const hasBasicSkuData = sku?.id && sku?.name && sku?.product_id

    // Allow all SKUs to pass through - don't filter by inventory level
    // This prevents over-filtering that removes valid SKUs
    return hasBasicSkuData
}

/**
 * Comprehensive validation function for product filtering
 * @param {Object} product - The product object
 * @returns {boolean} - True if product passes all validation checks
 */
export const validateNostoProduct = (product) => {
    const hasValidParent = hasValidParentProduct(product)
    const hasValidSkus = hasValidAvailableSkus(product)
    const hasValidStructure = hasValidProductStructure(product)

    return hasValidParent && hasValidSkus && hasValidStructure
}

/**
 * Comprehensive validation function for product tile rendering
 * @param {Object} product - The product object
 * @param {Object} sku - The SKU object
 * @returns {boolean} - True if product and SKU are valid for rendering
 */
export const validateNostoProductTile = (product, sku) => {
    const hasValidParent = hasValidParentProduct(product)
    const hasValidStructure = hasValidProductStructure(product)
    const hasValidSku = hasValidSkuData(sku, product)

    return hasValidParent && hasValidStructure && hasValidSku
}

/**
 * **MAIN FILTERING FUNCTION** - Filters array of products/hits based on validation criteria
 * @param {Array} products - Array of product objects
 * @param {Object} options - Filtering options
 * @param {boolean} options.includeOutOfStock - Whether to include out of stock products (default: false)
 * @param {boolean} options.enableLogging - Whether to log filtered products (default: true)
 * @param {string} options.context - Context for logging (default: 'Unknown')
 * @returns {Array} - Filtered array of valid products
 */
export const filterNostoProducts = (products, options = {}) => {
    const {
        includeOutOfStock = false,
        enableLogging = true,
        context = 'Unknown'
    } = options

    if (!Array.isArray(products)) {
        return []
    }

        return products.filter(product => {
        // Use regular variable assignment instead of type assertion for JS compatibility
        const prod = product

        // Check for valid parent product or marketplace product
        const hasValidParent = hasValidParentProduct(prod)

        // Check if this is a marketplace product
        const isMarketplaceProduct = prod?.brand && prod.brand !== 'Monica + Andy'

        // Define validation variables for debug logging
        // Note: Marketplace products may not have skus (or just a default sku)
        const hasAvailableSkus = prod?.skus?.some(sku => sku.inventoryLevel > 0) || false
        const hasBasicStructure = !!(prod?.productId && prod?.name)
        const isValidProduct = hasValidParent && hasBasicStructure

        // TEMPORARILY DISABLE ALL CLIENT-SIDE VALIDATION
        // Since server-side filtering is already working, skip client-side validation
        const passesValidation = true

        // Special debug logging for specific product
        if (prod?.productId === '7655777239102' && enableLogging) {
            console.log(`🔍 DETAILED DEBUG for product 7655777239102:`, {
                productId: prod?.productId,
                name: prod?.name,
                availability: prod?.availability,
                inventoryLevel: prod?.inventoryLevel,
                hasValidParent,
                hasAvailableSkus,
                hasBasicStructure,
                isValidProduct,
                passesValidation,
                skuDetails: prod?.skus?.map(sku => ({
                    id: sku.id,
                    name: sku.name,
                    inventoryLevel: sku.inventoryLevel,
                    hasBasicData: !!(sku.id && sku.name),
                    passesInventoryCheck: sku.inventoryLevel > 0
                })) || []
            })
        }

        // Debug logging for filtered products
        if (!passesValidation && enableLogging) {
            // Check if this product has any SKUs with inventory > 0
            const hasSkusWithInventory = prod?.skus?.some(sku => sku.inventoryLevel > 0)

            if (hasSkusWithInventory) {
                console.log(`🚨 INCORRECTLY FILTERED - Product with inventory being removed in ${context}: ${prod?.name || 'Unknown'}`, {
                    productId: prod?.productId,
                    brand: prod?.brand,
                    availability: prod?.availability,
                    productInventoryLevel: prod?.inventoryLevel,
                    hasValidParent,
                    hasAvailableSkus,
                    hasBasicStructure,
                    isValidProduct,
                    skuCount: prod?.skus?.length || 0,
                    skusWithInventory: prod?.skus?.filter(sku => sku.inventoryLevel > 0).map(sku => ({
                        id: sku.id,
                        name: sku.name,
                        inventoryLevel: sku.inventoryLevel
                    })) || []
                })
            } else {
                console.log(`Filtering out product in ${context}: ${prod?.name || 'Unknown'}`, {
                    productId: prod?.productId,
                    brand: prod?.brand,
                    availability: prod?.availability,
                    hasValidParent,
                    hasAvailableSkus,
                    hasBasicStructure,
                    isValidProduct,
                    tags1: prod?.tags1,
                    skuCount: prod?.skus?.length || 0
                })
            }
        }

        // Additional debug logging for marketplace products
        if (isMarketplaceProduct && enableLogging) {
            console.log(`Marketplace product validation in ${context}: ${prod?.name || 'Unknown'}`, {
                productId: prod?.productId,
                brand: prod?.brand,
                availability: prod?.availability,
                hasValidParent,
                hasAvailableSkus,
                hasBasicStructure,
                isValidProduct,
                passesValidation,
                skuCount: prod?.skus?.length || 0
            })
        }

        return passesValidation
    })
}

/**
 * Debug logging function for validation failures
 * @param {Object} product - The product object
 * @param {Object} sku - The SKU object (optional)
 * @param {string} context - Context where validation failed
 */
export const logValidationFailure = (product, sku = null, context = 'Unknown') => {
    const validationResult = {
        context,
        productId: product?.productId,
        productName: product?.name,
        skuId: sku?.id,
        skuName: sku?.name,
        hasValidParent: hasValidParentProduct(product),
        hasValidSkus: hasValidAvailableSkus(product),
        hasValidStructure: hasValidProductStructure(product),
        tags1: product?.tags1,
        skuCount: product?.skus?.length || 0,
        skuInventory: sku?.inventoryLevel
    }

    console.log(`Validation failed in ${context}:`, validationResult)

    return validationResult
}
